// Centralized date understanding for JVVNL search. Pure + deterministic: every
// function takes `now` so there are no hidden time dependencies. Adding a new
// phrase = one entry in DATE_ALIASES (or, for d+N, the one bounded regex below).
// NOT an AI system: static tables + bounded regexes, O(query length).

export interface DateMatch {
  /** YYYY-MM-DD for a single day. */
  date?: string;
  /** YYYY-MM-DD range start. */
  startDate?: string;
  /** YYYY-MM-DD range end. */
  endDate?: string;
  /** Display label for the pill, e.g. "Tomorrow", "25 Jun 2026", "Next Week". */
  label: string;
  /** Indices (into the tokenized query) consumed by this match — removed by the pre-pass. */
  matchedTokens: number[];
}

// ── date math helpers (local, no deps) ──────────────────────────────────────

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** Monday=0 … Sunday=6 (week starts Monday). */
function mondayIndex(d: Date): number {
  return (d.getDay() + 6) % 7;
}

function startOfWeek(d: Date): Date {
  return addDays(d, -mondayIndex(d));
}

function endOfWeek(d: Date): Date {
  return addDays(startOfWeek(d), 6);
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function startOfQuarter(d: Date): Date {
  return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
}

function endOfQuarter(d: Date): Date {
  const s = startOfQuarter(d);
  return new Date(s.getFullYear(), s.getMonth() + 3, 0);
}

/** Indian FY: Apr 1 – Mar 31. The FY containing `d`. */
function fyRange(d: Date): [Date, Date] {
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1; // Jan-Mar belongs to prev FY start year
  return [new Date(y, 3, 1), new Date(y + 1, 2, 31)];
}

function nextWeekday(d: Date, targetDow: number): Date {
  // targetDow: 0=Sun ... 6=Sat (JS getDay). Next occurrence (today counts only if same day => today).
  const diff = (targetDow - d.getDay() + 7) % 7;
  return addDays(d, diff);
}

function dayMatch(d: Date, label: string, tokens: number[]): DateMatch {
  return { date: ymd(d), label, matchedTokens: tokens };
}

function rangeMatch(a: Date, b: Date, label: string, tokens: number[]): DateMatch {
  return { startDate: ymd(a), endDate: ymd(b), label, matchedTokens: tokens };
}

// ── month + weekday tables ──────────────────────────────────────────────────

const MONTHS: Record<string, number> = {
  january: 0, jan: 0, february: 1, feb: 1, march: 2, mar: 2, april: 3, apr: 3,
  may: 4, june: 5, jun: 5, july: 6, jul: 6, august: 7, aug: 7,
  september: 8, sep: 8, sept: 8, october: 9, oct: 9, november: 10, nov: 10,
  december: 11, dec: 11,
};
const MONTH_LABEL = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const WEEKDAYS: Record<string, number> = {
  sunday: 0, sun: 0, monday: 1, mon: 1, tuesday: 2, tue: 2, tues: 2,
  wednesday: 3, wed: 3, thursday: 4, thu: 4, thurs: 4, friday: 5, fri: 5,
  saturday: 6, sat: 6,
};
const WEEKDAY_LABEL = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];

// ── DATE_ALIASES: single-token + multi-word phrases -> resolver descriptors ────
// The ONE place phrases are defined. `resolve` returns a partial DateMatch
// (date or range + label); matchedTokens is filled by the caller. Phrases are
// matched longest-first so "day after tomorrow" beats "tomorrow".

type AliasResolve = (now: Date) => Omit<DateMatch, "matchedTokens">;

export const DATE_ALIASES: Record<string, AliasResolve> = {
  // relative single day
  "today": (n) => dayMatchPartial(n, "Today"),
  "tomorrow": (n) => dayMatchPartial(addDays(n, 1), "Tomorrow"),
  "yesterday": (n) => dayMatchPartial(addDays(n, -1), "Yesterday"),
  "day after tomorrow": (n) => dayMatchPartial(addDays(n, 2), "Day After Tomorrow"),
  // operational single day
  "day ahead": (n) => dayMatchPartial(addDays(n, 1), "Day Ahead"),
  "week ahead": (n) => dayMatchPartial(addDays(n, 7), "Week Ahead"),
  "month ahead": (n) => dayMatchPartial(new Date(n.getFullYear(), n.getMonth() + 1, n.getDate()), "Month Ahead"),
  // ranges -- week
  "this week": (n) => rangePartial(startOfWeek(n), endOfWeek(n), "This Week"),
  "current week": (n) => rangePartial(startOfWeek(n), endOfWeek(n), "Current Week"),
  "next week": (n) => rangePartial(startOfWeek(addDays(n, 7)), endOfWeek(addDays(n, 7)), "Next Week"),
  "upcoming week": (n) => rangePartial(startOfWeek(addDays(n, 7)), endOfWeek(addDays(n, 7)), "Upcoming Week"),
  "rest of week": (n) => rangePartial(n, endOfWeek(n), "Rest of Week"),
  "remaining week": (n) => rangePartial(n, endOfWeek(n), "Remaining Week"),
  // ranges -- month
  "this month": (n) => rangePartial(startOfMonth(n), endOfMonth(n), "This Month"),
  "current month": (n) => rangePartial(startOfMonth(n), endOfMonth(n), "Current Month"),
  "next month": (n) => {
    const nm = new Date(n.getFullYear(), n.getMonth() + 1, 1);
    return rangePartial(startOfMonth(nm), endOfMonth(nm), "Next Month");
  },
  "rest of month": (n) => rangePartial(n, endOfMonth(n), "Rest of Month"),
  "remaining month": (n) => rangePartial(n, endOfMonth(n), "Remaining Month"),
  // ranges -- quarter
  "this quarter": (n) => rangePartial(startOfQuarter(n), endOfQuarter(n), "This Quarter"),
  "next quarter": (n) => {
    const nq = new Date(n.getFullYear(), Math.floor(n.getMonth() / 3) * 3 + 3, 1);
    return rangePartial(startOfQuarter(nq), endOfQuarter(nq), "Next Quarter");
  },
  // ranges -- FY
  "current fy": (n) => { const [a, b] = fyRange(n); return rangePartial(a, b, "Current FY"); },
  "next fy": (n) => { const [a, b] = fyRange(new Date(n.getFullYear() + 1, n.getMonth(), n.getDate())); return rangePartial(a, b, "Next FY"); },
};

function dayMatchPartial(d: Date, label: string): Omit<DateMatch, "matchedTokens"> {
  return { date: ymd(d), label };
}
function rangePartial(a: Date, b: Date, label: string): Omit<DateMatch, "matchedTokens"> {
  return { startDate: ymd(a), endDate: ymd(b), label };
}

// Domain keywords that license a BARE month being read as a date (section 3.2).
const MONTH_GATE_WORDS = new Set([
  "forecast", "temperature", "humidity", "wind", "rain", "weather",
  "generation", "load", "radiation", "cloud", "sunshine", "precipitation",
  "solar", "demand",
]);

// Longest phrase (in words) in DATE_ALIASES, to bound the span scan.
const MAX_ALIAS_WORDS = Math.max(
  ...Object.keys(DATE_ALIASES).map((k) => k.split(" ").length),
);

export { MONTHS, MONTH_LABEL, WEEKDAYS, WEEKDAY_LABEL, MONTH_GATE_WORDS, MAX_ALIAS_WORDS, ymd, addDays };

// ── parseDate ───────────────────────────────────────────────────────────────
// Specificity tiers (higher wins; ties → leftmost). §3.1.
const TIER_NUMERIC = 6;   // explicit numeric date (25/06/2026, 2026-06-25, …)
const TIER_MONTH_DAY = 5; // "25 june" / "june 25"
const TIER_OFFSET = 4;    // d+N
const TIER_WEEKDAY = 3;   // friday / fri
const TIER_REL_DAY = 2;   // today / tomorrow / day ahead
const TIER_RANGE = 1;     // next week / june (bare month) / fy …

interface Candidate {
  tier: number;
  start: number; // first matched token index (for leftmost tie-break)
  match: DateMatch;
}

const NUMERIC_RE =
  /^(\d{4})-(\d{1,2})-(\d{1,2})$|^(\d{1,2})[/.\-](\d{1,2})[/.\-](\d{4})$/;

function parseNumericToken(tok: string, idx: number): DateMatch | null {
  const m = tok.match(NUMERIC_RE);
  if (!m) return null;
  let y: number, mo: number, d: number;
  if (m[1]) {
    // ISO yyyy-mm-dd (unambiguous)
    y = +m[1]; mo = +m[2]; d = +m[3];
  } else {
    // a[sep]b[sep]yyyy — default dd/mm; fall back to mm/dd only when a>12 && b<=12
    const a = +m[4], b = +m[5];
    y = +m[6];
    if (a > 12 && b <= 12) { mo = b; d = a; }      // a can only be a day
    else if (a <= 12 && b > 12) { mo = a; d = b; } // b can only be a day → mm/dd
    else { d = a; mo = b; }                         // ambiguous → dd/mm default
  }
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, mo - 1, d);
  const label = `${String(d).padStart(2, "0")} ${MONTH_LABEL[mo - 1].slice(0, 3)} ${y}`;
  return dayMatch(dt, label, [idx]);
}

const OFFSET_RE = /^d\+(\d{1,3})$/;

function dateLabelDayMonth(d: Date): string {
  return `${String(d.getDate()).padStart(2, "0")} ${MONTH_LABEL[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
}

/**
 * Find the single best date in the token list. Returns null when no date.
 * Bare-month gating (§3.2): a lone month word is a date only when the token
 * list also contains a MONTH_GATE_WORDS keyword.
 */
export function parseDate(tokens: string[], now: Date): DateMatch | null {
  const cands: Candidate[] = [];
  const hasGateWord = tokens.some((t) => MONTH_GATE_WORDS.has(t));

  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i];

    // numeric
    const num = parseNumericToken(tok, i);
    if (num) { cands.push({ tier: TIER_NUMERIC, start: i, match: num }); continue; }

    // d+N
    const off = tok.match(OFFSET_RE);
    if (off) {
      const n = +off[1];
      const d = addDays(now, n);
      cands.push({ tier: TIER_OFFSET, start: i, match: dayMatch(d, `+${n} day${n === 1 ? "" : "s"}`, [i]) });
      continue;
    }

    // month + day (either order): "25 june" / "june 25"
    const monNum = MONTHS[tok];
    if (monNum !== undefined) {
      const prev = i > 0 ? Number(tokens[i - 1]) : NaN;
      const next = i + 1 < tokens.length ? Number(tokens[i + 1]) : NaN;
      if (Number.isInteger(prev) && prev >= 1 && prev <= 31) {
        const dt = new Date(now.getFullYear(), monNum, prev);
        cands.push({ tier: TIER_MONTH_DAY, start: i - 1, match: dayMatch(dt, dateLabelDayMonth(dt), [i - 1, i]) });
        continue;
      }
      if (Number.isInteger(next) && next >= 1 && next <= 31) {
        const dt = new Date(now.getFullYear(), monNum, next);
        cands.push({ tier: TIER_MONTH_DAY, start: i, match: dayMatch(dt, dateLabelDayMonth(dt), [i, i + 1]) });
        continue;
      }
      // bare month → range, gated (§3.2)
      if (hasGateWord) {
        const a = new Date(now.getFullYear(), monNum, 1);
        const b = new Date(now.getFullYear(), monNum + 1, 0);
        cands.push({ tier: TIER_RANGE, start: i, match: rangeMatch(a, b, MONTH_LABEL[monNum], [i]) });
      }
      continue;
    }

    // weekday
    const dow = WEEKDAYS[tok];
    if (dow !== undefined) {
      const d = nextWeekday(now, dow);
      cands.push({ tier: TIER_WEEKDAY, start: i, match: dayMatch(d, WEEKDAY_LABEL[dow], [i]) });
      continue;
    }
  }

  // multi-word + single-word DATE_ALIASES (phrases longest-first)
  for (let span = MAX_ALIAS_WORDS; span >= 1; span--) {
    for (let i = 0; i + span <= tokens.length; i++) {
      const phrase = tokens.slice(i, i + span).join(" ");
      const resolve = DATE_ALIASES[phrase];
      if (!resolve) continue;
      const partial = resolve(now);
      const idxs: number[] = [];
      for (let k = 0; k < span; k++) idxs.push(i + k);
      const tier = partial.date ? TIER_REL_DAY : TIER_RANGE;
      cands.push({ tier, start: i, match: { ...partial, matchedTokens: idxs } });
    }
  }

  if (cands.length === 0) return null;
  cands.sort((a, b) => b.tier - a.tier || a.start - b.start);
  return cands[0].match;
}

// -- suggestions + ghost completion -------------------------------------------

// Display labels offered as suggestion pills, in priority order. Single source
// for both the pill strip and ghost completion.
const SUGGEST_LABELS = [
  "Today", "Tomorrow", "Yesterday", "Day After Tomorrow",
  "This Week", "Next Week", "This Month", "Next Month",
  "This Quarter", "Next Quarter", "Current FY", "Next FY",
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday",
  ...MONTH_LABEL,
];

/**
 * Date suggestion labels for a partial last token (>=2 chars). Includes concrete
 * month-day examples for a bare month prefix ("jun" -> June, June <today>, June <eom>).
 */
export function dateSuggestions(prefix: string, now: Date): string[] {
  const p = prefix.trim().toLowerCase();
  if (p.length < 2) return [];
  const out: string[] = [];
  for (const label of SUGGEST_LABELS) {
    if (label.toLowerCase().startsWith(p)) out.push(label);
  }
  // For a month prefix, add a couple of concrete days (spec: "jun" -> June, June 25, June 30).
  const monthHit = Object.keys(MONTHS).find((m) => m.startsWith(p) && m.length >= 3);
  if (monthHit) {
    const monNum = MONTHS[monthHit];
    const eom = new Date(now.getFullYear(), monNum + 1, 0).getDate();
    const today = now.getDate();
    const label = MONTH_LABEL[monNum];
    if (today <= eom) out.push(`${label} ${today}`);
    out.push(`${label} ${eom}`);
  }
  // De-dup, cap.
  return Array.from(new Set(out)).slice(0, 6);
}

/**
 * Ghost-text completion for a date alias: given the raw last token (>=2 chars),
 * return the remaining characters of the single best matching label, lowercased
 * to append inline. Empty when ambiguous or no match.
 */
export function dateCompletion(lastToken: string): string {
  const p = lastToken.trim().toLowerCase();
  if (p.length < 2) return "";
  const matches = SUGGEST_LABELS
    .map((l) => l.toLowerCase())
    .filter((l) => l !== p && l.startsWith(p));
  // Empty when ambiguous: only ghost-complete when exactly one label matches,
  // so "to" (today + tomorrow) shows no surprising completion, while "tomor"
  // (one match) completes inline.
  if (matches.length !== 1) return "";
  return matches[0].slice(p.length);
}
